import React, { PropsWithChildren, ReactElement } from 'react'
import clsx from 'clsx'
import { Select as AntSelect } from 'antd'
import {
  SelectProps as AntSelectProps,
  OptionProps as AntOptionProps,
  SelectValue
} from 'antd/lib/select'
import { Tag } from '../Tag/Tag'

import s from './Select.module.css'

export type SelectProps<T = string> = AntSelectProps<T> & {
  updatePosition?: (val: number) => void
  helperText?: string
  error?: string
  customTag?: string
  customTagLabel?: string
  closeIcon?: ReactElement
  onRemoveTag?: (label: string) => void
}
type OptionProps = AntOptionProps

export const Select = <T extends SelectValue = string>({
  id,
  children,
  notFoundContent,
  allowClear,
  bordered,
  className,
  defaultValue,
  disabled,
  maxTagCount,
  mode,
  onBlur,
  onChange,
  options,
  placeholder,
  showArrow,
  showSearch,
  optionFilterProp,
  size,
  value,
  onSearch,
  updatePosition,
  filterOption,
  tokenSeparators,
  dropdownRender,
  dropdownStyle,
  dropdownClassName,
  onFocus,
  dropdownMatchSelectWidth,
  loading,
  open,
  optionLabelProp,
  autoFocus,
  suffixIcon,
  helperText,
  error,
  onSelect,
  customTag,
  customTagLabel,
  closeIcon,
  onRemoveTag,
  onDropdownVisibleChange,
  ...rest
}: PropsWithChildren<SelectProps<T>>): JSX.Element => {
  const tagRender = (props: any) => {
    const { label, closable, onClose, disabled: tagDisabled } = props

    // if the tag is closable means that it is the maxCount placeholder
    // excluding the tags which are disabled
    if (!closable && !tagDisabled) {
      return <span className={s.maxPlaceholder}>{label}</span>
    }

    return (
      <Tag
        className={clsx(s.selectTag, customTag)}
        closable={closable}
        onClose={target => {
          onClose(target)
          onRemoveTag?.(label)
        }}
        {...(closeIcon && {
          closeIcon
        })}
      >
        <span className={clsx(s.selectTagLabel, customTagLabel)}>{label}</span>
      </Tag>
    )
  }

  const controlledProps = open !== undefined ? { open } : {}
  const supportingText = error ?? helperText

  return (
    <div className={s.wrapper}>
      <AntSelect<T>
        id={id}
        className={clsx(className, {
          [s.errorBorder]: !!error
        })}
        filterOption={filterOption}
        onSearch={onSearch}
        bordered={bordered}
        defaultValue={defaultValue}
        disabled={disabled}
        mode={mode}
        onChange={onChange}
        options={options}
        optionFilterProp={optionFilterProp}
        placeholder={placeholder}
        showArrow={showArrow}
        showSearch={showSearch}
        size={size}
        maxTagCount={maxTagCount}
        maxTagPlaceholder={test => {
          return `+${test.length}`
        }}
        tagRender={tagRender}
        value={value}
        notFoundContent={notFoundContent}
        allowClear={allowClear}
        getPopupContainer={trigger => trigger.parentNode}
        tokenSeparators={tokenSeparators}
        dropdownRender={dropdownRender}
        dropdownStyle={dropdownStyle}
        onFocus={e => {
          updatePosition?.(e.target.offsetTop)
          onFocus?.(e)
        }}
        onBlur={e => {
          updatePosition?.(0)
          onBlur?.(e)
        }}
        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
        dropdownClassName={dropdownClassName}
        loading={loading}
        optionLabelProp={optionLabelProp}
        autoFocus={autoFocus}
        suffixIcon={suffixIcon}
        onSelect={onSelect}
        onDropdownVisibleChange={onDropdownVisibleChange}
        {...controlledProps}
        {...rest}
      >
        {children}
      </AntSelect>
      {supportingText && (
        <span
          className={clsx(s.helperText, {
            [s.error]: !!error
          })}
        >
          {supportingText}
        </span>
      )}
    </div>
  )
}

export const Option: React.FC<OptionProps> = ({
  children,
  title,
  value,
  className
}) => (
  <AntSelect.Option title={title} value={value} className={className}>
    {children}
  </AntSelect.Option>
)
